import React from 'react';
import { useSidebar } from 'src/presentations/components/organisms/sidebar/useSidebar';
import { SnackbarProps } from './types';

/**
 * A reusable Snackbar component for displaying notifications
 */
const Snackbar: React.FC<SnackbarProps> = ({
  className = '',
  icon,
  content,
  actions,
  showDivider = false,
  position = 'bottom-left',
  zIndex = 20,
  autoSidebarPlacement = false,
}) => {
  // Use sidebar context for dynamic positioning
  const { isCollapsed, sidebarWidth, isXlBreakpoint } = useSidebar();

  // Get position styles and classes
  const getPositionStyles = () => {
    // Define position style type
    type PositionStyle = {
      top?: string;
      bottom?: string;
      left?: string;
      right?: string;
      transform?: string;
      transition?: string;
    };

    // Base positions for non-auto placement
    const basePositions: Record<SnackbarProps['position'], PositionStyle> = {
      'bottom-left': { bottom: '16px', left: '16px' },
      'bottom-right': { bottom: '16px', right: '16px' },
      'top-left': { top: '16px', left: '16px' },
      'top-right': { top: '16px', right: '16px' },
      'bottom-center': { bottom: '16px', left: '50%', transform: 'translateX(-50%)' },
      'top-center': { top: '16px', left: '50%', transform: 'translateX(-50%)' },
    };

    // Get the base position style
    const basePositionStyle = { ...basePositions[position] };

    // For auto sidebar placement, adjust left position based on sidebar width
    if (autoSidebarPlacement) {
      // Use sidebar width from context for dynamic positioning
      // Add extra padding (44px for XL screens, 36px for smaller screens) for better spacing
      const extraPadding = isXlBreakpoint ? 44 : 36;
      const positionStyle = { ...basePositionStyle };
      positionStyle.left = `${sidebarWidth + extraPadding}px`;

      // We can use isCollapsed for any additional styling if needed
      // For example, we could add different transition speeds based on collapsed state
      if (isCollapsed) {
        // Fast transition when sidebar is collapsed
        positionStyle.transition = 'left 0.2s ease-out';
      } else {
        // Slower transition when sidebar is expanded
        positionStyle.transition = 'left 0.3s ease-in-out';
      }
      return positionStyle;
    }

    return basePositionStyle;
  };

  // Get position classes (only for non-dynamic parts)
  const getPositionClasses = () => {
    // For center positions that need transform
    if (position === 'bottom-center' || position === 'top-center') {
      return position === 'bottom-center' ? 'bottom-16' : 'top-16';
    }
    return '';
  };

  return (
    <div
      className={`fixed z-${zIndex} px-16 py-10 rounded-6 bg-[#202020] text-white flex items-center ${getPositionClasses()} ${className}`}
      style={{
        ...getPositionStyles(),
        transition: 'all 300ms ease-in-out',
      }}
    >
      {/* Icon section */}
      {icon && <div className='flex items-center mr-10'>{icon}</div>}

      {/* Content section */}
      <div className='flex items-center'>{content}</div>

      {actions && (
        <div className='ml-auto flex items-center gap-24'>
          {showDivider && <div className='w-1 h-16 bg-neutral-70 mx-10' />}
          <div className='flex items-center gap-16'>{actions}</div>
        </div>
      )}
    </div>
  );
};

export default Snackbar;
